import { Observable, EMPTY } from 'rxjs';
import { mergeMap, finalize } from 'rxjs/operators';

/**
 * Custom rxjs operator which only keeps one observable per parameter value.
 * @param project A function that, when applied to an item emitted by the source Observable, returns an Observable.
 * @param keySelector An optional function that, when applied to an item emitted by the source Observable,
 * returns a string which is the key to uniquely identify the observable params.
 */
export function keySensitiveSwitchMap<T, R>(
  project: (value: T, index: number) => Observable<R>,
  keySelector: ((value: T) => string) | undefined = undefined
) {
  const ongoingRequests = new Map<string, Observable<R>>();

  return (source: Observable<T>) =>
    source.pipe(
      mergeMap((value, index) => {
        let key;
        if (keySelector) {
          key = keySelector(value);
        } else {
          key = JSON.stringify(value);
        }

        // If we already have this request ongoing, return empty
        if (ongoingRequests.has(key)) {
          return EMPTY;
        }

        const request$ = project(value, index).pipe(finalize(() => ongoingRequests.delete(key)));

        ongoingRequests.set(key, request$);
        return request$;
      })
    );
}
